/**
 * Shuttle Checkout Form Validation
 * Handles form field validation with real-time feedback
 * 
 * @package A2A_Theme
 */

class CheckoutFormValidator {
    constructor() {
        this.validationRules = {
            firstName: {
                required: true,
                minLength: 2,
                maxLength: 50,
                pattern: /^[a-zA-ZÀ-ÿ\u0100-\u017F\u0400-\u04FF\s\-\']+$/,
                messageKey: 'first-name-invalid'
            },
            lastName: {
                required: true,
                minLength: 2,
                maxLength: 50,
                pattern: /^[a-zA-ZÀ-ÿ\u0100-\u017F\u0400-\u04FF\s\-\']+$/,
                messageKey: 'last-name-invalid'
            },
            email: {
                required: true,
                pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                messageKey: 'email-invalid'
            },
            phone: {
                required: true,
                minLength: 8,
                maxLength: 15,
                pattern: /^[\d\s\-\+\(\)]+$/,
                messageKey: 'phone-invalid'
            }
        };
        
        // Don't initialize immediately - wait for explicit init call
    }
    
    init() {
        // Ensure jQuery is available before setting up validation
        if (typeof jQuery === 'undefined') {
            console.error('jQuery not available for checkout validation');
            return;
        }
        this.setupRealTimeValidation();
    }
    
    /**
     * Get translation string from PHP template
     */
    getTranslationString(key) {
        const translationElements = jQuery(`[data-string="${key}"]`);
        if (translationElements.length > 0) {
            return jQuery(translationElements[0]).text();
        }
        return key; // Fallback to key if translation not found
    }
    
    setupRealTimeValidation() {
        // Email validation
        jQuery('#passenger-email').on('blur', () => {
            this.validateField('email', jQuery('#passenger-email').val(), '#email-error');
        });
        
        // First name validation
        jQuery('#passenger-first-name').on('blur', () => {
            this.validateField('firstName', jQuery('#passenger-first-name').val(), '#first-name-error');
        });
        
        // Last name validation
        jQuery('#passenger-last-name').on('blur', () => {
            this.validateField('lastName', jQuery('#passenger-last-name').val(), '#last-name-error');
        });
        
        // Phone validation
        jQuery('#passenger-phone').on('blur', () => {
            const phoneNumber = jQuery('#passenger-phone').val();
            this.validateField('phone', phoneNumber, '#phone-error');
        });
        
        // Clear errors on focus
        jQuery('.shuttle-checkout__input').on('focus', function() {
            jQuery(this).removeClass('shuttle-checkout__input--error');
            jQuery(this).siblings('.shuttle-checkout__field-error').removeClass('shuttle-checkout__field-error--visible');
        });
    }
    
    validateField(fieldName, value, errorElementId) {
        const rule = this.validationRules[fieldName];
        if (!rule) return { valid: true };
        
        const validation = this.performValidation(rule, value, fieldName);
        
        // Update UI
        this.updateFieldUI(fieldName, validation, errorElementId);
        
        return validation;
    }
    
    performValidation(rule, value, fieldName) {
        // Required check
        if (rule.required && (!value || value.trim() === '')) {
            return { 
                valid: false, 
                message: `${this.getFieldDisplayName(fieldName)} ${this.getTranslationString('required-suffix')}` 
            };
        }
        
        // Skip other validations if field is empty and not required
        if (!value || value.trim() === '') {
            return { valid: true };
        }
        
        // Length checks
        if (rule.minLength && value.length < rule.minLength) {
            return { valid: false, message: this.getTranslationString(rule.messageKey) };
        }
        
        if (rule.maxLength && value.length > rule.maxLength) {
            return { valid: false, message: this.getTranslationString(rule.messageKey) };
        }
        
        // Pattern check
        if (rule.pattern && !rule.pattern.test(value)) {
            return { valid: false, message: this.getTranslationString(rule.messageKey) };
        }
        
        return { valid: true };
    }
    
    updateFieldUI(fieldName, validation, errorElementId) {
        const inputElement = this.getInputElement(fieldName);
        const errorElement = jQuery(errorElementId);
        
        if (validation.valid) {
            inputElement.removeClass('shuttle-checkout__input--error');
            errorElement.removeClass('shuttle-checkout__field-error--visible').text('');
        } else {
            inputElement.addClass('shuttle-checkout__input--error');
            errorElement.addClass('shuttle-checkout__field-error--visible').text(validation.message);
        }
    }
    
    getInputElement(fieldName) {
        const inputMap = {
            email: jQuery('#passenger-email'),
            firstName: jQuery('#passenger-first-name'),
            lastName: jQuery('#passenger-last-name'),
            phone: jQuery('#passenger-phone')
        };
        
        return inputMap[fieldName] || jQuery();
    }
    
    getFieldDisplayName(fieldName) {
        const displayNameKeys = {
            email: 'field-email',
            firstName: 'field-first-name',
            lastName: 'field-last-name',
            phone: 'field-phone-number'
        };
        
        const key = displayNameKeys[fieldName];
        return key ? this.getTranslationString(key) : fieldName;
    }
    
    validateForm(formData) {
        const errors = {};
        let isValid = true;
        
        // Validate each field
        for (const [fieldName, value] of Object.entries(formData)) {
            if (this.validationRules[fieldName]) {
                const validation = this.performValidation(this.validationRules[fieldName], value, fieldName);
                if (!validation.valid) {
                    errors[fieldName] = validation.message;
                    isValid = false;
                }
            }
        }
        
        return { isValid, errors };
    }
    
    showFormErrors(errors) {
        // Clear all previous errors
        jQuery('.shuttle-checkout__input').removeClass('shuttle-checkout__input--error');
        jQuery('.shuttle-checkout__field-error').removeClass('shuttle-checkout__field-error--visible').text('');
        
        // Show new errors
        for (const [fieldName, message] of Object.entries(errors)) {
            const inputElement = this.getInputElement(fieldName);
            const errorElementId = this.getErrorElementId(fieldName);
            
            inputElement.addClass('shuttle-checkout__input--error');
            jQuery(errorElementId).addClass('shuttle-checkout__field-error--visible').text(message);
        }
        
        // Scroll to first error
        if (Object.keys(errors).length > 0) {
            const firstErrorField = Object.keys(errors)[0];
            const firstErrorElement = this.getInputElement(firstErrorField);
            if (firstErrorElement.length) {
                jQuery('html, body').animate({
                    scrollTop: firstErrorElement.offset().top - 100
                }, 300);
            }
        }
    }
    
    getErrorElementId(fieldName) {
        const errorMap = {
            email: '#email-error',
            firstName: '#first-name-error',
            lastName: '#last-name-error',
            phone: '#phone-error'
        };
        
        return errorMap[fieldName] || '';
    }
    
    getFormData() {
        // Get country code ID and phone number without dial code from phone input instance
        let countryCodeId = null;
        let phoneWithoutDialCode = jQuery('#passenger-phone').val().trim();
        let countryCode = '+44'; // Default
        let phoneCountryCode = 'gb'; // Default ISO code
        
        if (window.shuttleCheckoutManager && window.shuttleCheckoutManager.phoneInputInstance) {
            const phoneInstance = window.shuttleCheckoutManager.phoneInputInstance;
            countryCodeId = phoneInstance.getCountryCodeId();
            phoneWithoutDialCode = phoneInstance.getPhoneNumberWithoutDialCode();
            
            // Get selected country details
            const selectedCountry = phoneInstance.getSelectedCountry();
            if (selectedCountry) {
                countryCode = selectedCountry.dialCode;
                phoneCountryCode = selectedCountry.code;
            }
        }
        
        return {
            email: jQuery('#passenger-email').val().trim(),
            firstName: jQuery('#passenger-first-name').val().trim(),
            lastName: jQuery('#passenger-last-name').val().trim(),
            phone: phoneWithoutDialCode,
            countryCodeId: countryCodeId,
            countryCode: countryCode,
            phoneCountryCode: phoneCountryCode
        };
    }
    
    clearAllErrors() {
        jQuery('.shuttle-checkout__input').removeClass('shuttle-checkout__input--error');
        jQuery('.shuttle-checkout__field-error').removeClass('shuttle-checkout__field-error--visible').text('');
    }
}
